/**
 * 「書類の仕上げキュー」: 実施業務からの一括作成で生まれた複数の下書きを、
 * 1件ずつ体裁を整えて発行するまで案内し続けるための順序付きキュー。
 *
 * 一括作成は司法書士請求書・行政書士請求書・土地計算書・建物計算書・調査士請求書・見積書を
 * まとめて作るが、以前は先頭の1件へ遷移するだけで残りの下書きに戻る導線が無かった。
 * 遷移 state だと 発行→ダウンロード画面→戻る でキューが消えるため、
 * recentIssues.ts と同じく sessionStorage に持つ（リロードにも耐える）。
 */
const WINDOW_MS = 6 * 60 * 60 * 1000; // 6時間で失効（作りかけを翌日まで引きずらない）

export type DocQueue = {
  caseId:    string;
  /** 仕上げる順に並んだ書類ID */
  docIds:    string[];
  /** キュー開始時刻（失効判定用） */
  startedAt: number;
};

const storageKey = (caseId: string) => `doc-finish-queue:${caseId}`;

/** 一括作成の直後に呼ぶ。2件以上のときだけキューを張る（1件ならナビは不要） */
export function startDocQueue(caseId: string, docIds: string[]): void {
  try {
    if (docIds.length < 2) {
      clearDocQueue(caseId);
      return;
    }
    const queue: DocQueue = { caseId, docIds, startedAt: Date.now() };
    sessionStorage.setItem(storageKey(caseId), JSON.stringify(queue));
  } catch {
    // sessionStorage が使えない環境ではナビ無しで動作継続
  }
}

/** 有効なキューを返す。失効・未設定なら null */
export function getDocQueue(caseId: string | undefined): DocQueue | null {
  if (!caseId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(caseId));
    if (!raw) return null;
    const queue = JSON.parse(raw) as DocQueue;
    if (!Array.isArray(queue.docIds) || queue.docIds.length === 0) return null;
    if (Date.now() - queue.startedAt > WINDOW_MS) {
      clearDocQueue(caseId);
      return null;
    }
    return queue;
  } catch {
    return null;
  }
}

export function clearDocQueue(caseId: string): void {
  try {
    sessionStorage.removeItem(storageKey(caseId));
  } catch {
    // noop
  }
}

/**
 * キュー内での位置。キュー外の書類を開いている場合は null
 * （キューが張られていても無関係な書類ではナビを出さない）
 */
export function queuePosition(
  queue: DocQueue | null,
  docId: string | undefined,
): { index: number; total: number } | null {
  if (!queue || !docId) return null;
  const index = queue.docIds.indexOf(docId);
  if (index < 0) return null;
  return { index, total: queue.docIds.length };
}

/**
 * 発行済みになった書類をキューから外さずに「次に仕上げるべき書類」を求める。
 * 現在地より後ろを優先し、無ければ前方の未完了へ戻る（順不同で発行しても取りこぼさない）。
 *
 * @param isDone 「その書類はもう仕上がったか」（= 発行済みか）を返す関数
 */
export function nextPendingDocId(
  queue: DocQueue | null,
  currentDocId: string | undefined,
  isDone: (docId: string) => boolean,
): string | null {
  if (!queue) return null;
  const current = currentDocId ? queue.docIds.indexOf(currentDocId) : -1;
  const ordered = current >= 0
    ? [...queue.docIds.slice(current + 1), ...queue.docIds.slice(0, current)]
    : queue.docIds;
  return ordered.find((docId) => !isDone(docId)) ?? null;
}
